import { useEffect, useMemo, useState } from "react";
import type { Application, ConstructionTimelinePhase } from "../types";
import { constructionPlanTotalDays, ganttAxisScale, sortConstructionPhases } from "../utils/constructionTimeline";
import { useAppState } from "../context/AppStateContext";
import { fetchBeneficiaryTimelineProgress } from "../api/subcontractorPortalApi";
import {
  type PhasePortalPhotoBundle,
  portalPhotoBundleHasImage,
} from "../utils/timelineGanttPortalPhotos";

export type { PhasePortalPhotoBundle };

function formatApproxDays(days: number): string {
  const n = days < 10 ? String(days).padStart(2, "0") : String(days);
  return `Approx ${n} days`;
}

function GanttPortalThumbStrip({ bundle }: { bundle: PhasePortalPhotoBundle | undefined }) {
  if (!bundle) {
    return (
      <span className="beneficiary-gantt__portal-dash" aria-hidden>
        —
      </span>
    );
  }
  const urls = [bundle.photo1, bundle.photo2, bundle.photo3].filter(
    (u): u is string => typeof u === "string" && u.length > 12,
  );
  if (urls.length === 0) {
    return (
      <span className="beneficiary-gantt__portal-dash" aria-hidden>
        —
      </span>
    );
  }
  return (
    <div className="beneficiary-gantt__portal-thumb-strip">
      {urls.map((src, i) => (
        <a
          key={i}
          href={src}
          target="_blank"
          rel="noopener noreferrer"
          className="beneficiary-gantt__portal-thumb"
          title={`Open subcontractor portal photo ${i + 1}`}
          aria-label={`Open subcontractor portal photo ${i + 1} full size`}
        >
          <img src={src} alt="" />
        </a>
      ))}
    </div>
  );
}

export type ConstructionGanttViewProps = {
  phases: ConstructionTimelinePhase[];
  actualDays: Partial<Record<string, number>>;
  /** When true, omit the “no site data” line in metrics (master preview). */
  preview?: boolean;
  /** Per phase: images submitted from the subcontractor portal (timeline progress). */
  phasePortalPhotos?: Partial<Record<string, PhasePortalPhotoBundle>>;
};

/**
 * Construction Gantt — sequential plan from master phases; bar positions use a shared day axis.
 * Actuals keyed by phase id (e.g. from `application.constructionTimelineActualDays`).
 */
export function ConstructionGanttView({ phases, actualDays, preview, phasePortalPhotos }: ConstructionGanttViewProps) {
  const sorted = useMemo(() => sortConstructionPhases(phases), [phases]);
  const planTotal = constructionPlanTotalDays(sorted);

  const loggedTotalForAxis = useMemo(() => {
    let sum = 0;
    let any = false;
    for (const p of sorted) {
      const raw = actualDays[p.id];
      if (typeof raw === "number" && Number.isFinite(raw) && raw >= 0) {
        sum += raw;
        any = true;
      }
    }
    return any ? sum : undefined;
  }, [sorted, actualDays]);

  const { axisMax, ticks } = useMemo(
    () => ganttAxisScale(planTotal, { loggedTotalDays: loggedTotalForAxis }),
    [planTotal, loggedTotalForAxis],
  );

  const rows = useMemo(() => {
    let cum = 0;
    return sorted.map((p) => {
      const approx = Math.max(0, Math.floor(Number(p.approxDays)) || 0);
      const leftPct = axisMax > 0 ? (cum / axisMax) * 100 : 0;
      const widthPct = axisMax > 0 ? (approx / axisMax) * 100 : 0;
      cum += approx;
      const raw = actualDays[p.id];
      const actual = typeof raw === "number" && Number.isFinite(raw) && raw >= 0 ? raw : null;
      return { ...p, approxDays: approx, leftPct, widthPct, actual };
    });
  }, [sorted, actualDays, axisMax]);

  let actualSum = 0;
  let approxSumComparable = 0;
  let phasesWithActual = 0;
  for (const r of rows) {
    if (r.actual != null) {
      actualSum += r.actual;
      approxSumComparable += r.approxDays;
      phasesWithActual += 1;
    }
  }
  const varianceRecorded = phasesWithActual > 0 ? actualSum - approxSumComparable : null;
  const phaseCount = sorted.length;

  const hasPortalPhotoColumn = useMemo(() => {
    if (!phasePortalPhotos) return false;
    for (const b of Object.values(phasePortalPhotos)) {
      if (portalPhotoBundleHasImage(b)) return true;
    }
    return false;
  }, [phasePortalPhotos]);

  if (phaseCount === 0) {
    return (
      <div className="beneficiary-gantt beneficiary-gantt--empty" role="status">
        <p className="beneficiary-gantt__chart-title">Timeline tracking: Approx vs actual (days)</p>
        <p className="text-muted section-lead section-lead--tight">
          No construction levels defined yet. Add levels and approximate days under{" "}
          <strong>Projects → Timeline master</strong> to build the Gantt chart.
        </p>
      </div>
    );
  }

  return (
    <div
      className="beneficiary-gantt"
      role="img"
      aria-label={`Construction timeline approx ${planTotal} days total plan`}
    >
      <p className="beneficiary-gantt__chart-title">Timeline tracking: Approx vs actual (days)</p>
      <p className="beneficiary-gantt__duration">Approx plan duration: {planTotal} days</p>

      <div className="beneficiary-gantt__metrics" role="region" aria-label="Approx versus actual summary">
        <div className="beneficiary-gantt__metric">
          <span className="beneficiary-gantt__metric-label">Approx total (plan)</span>
          <span className="beneficiary-gantt__metric-value">{planTotal} d</span>
        </div>
        <div className="beneficiary-gantt__metric">
          <span className="beneficiary-gantt__metric-label">Actual total (logged)</span>
          <span className="beneficiary-gantt__metric-value">
            {phasesWithActual > 0 ? `${actualSum} d` : "—"}
            {phasesWithActual > 0 ? (
              <span className="beneficiary-gantt__metric-sub">
                {" "}
                ({phasesWithActual}/{phaseCount} phases)
              </span>
            ) : preview ? (
              <span className="beneficiary-gantt__metric-sub"> Plan preview</span>
            ) : (
              <span className="beneficiary-gantt__metric-sub"> No site data yet</span>
            )}
          </span>
        </div>
        <div className="beneficiary-gantt__metric">
          <span className="beneficiary-gantt__metric-label">Variance (on logged phases)</span>
          <span
            className={
              "beneficiary-gantt__metric-value" +
              (varianceRecorded == null
                ? ""
                : varianceRecorded > 0
                  ? " beneficiary-gantt__metric-value--behind"
                  : varianceRecorded < 0
                    ? " beneficiary-gantt__metric-value--ahead"
                    : " beneficiary-gantt__metric-value--neutral")
            }
          >
            {varianceRecorded == null
              ? "—"
              : `${varianceRecorded > 0 ? "+" : ""}${varianceRecorded} d ${varianceRecorded > 0 ? "(slower)" : varianceRecorded < 0 ? "(faster)" : "(on plan)"}`}
          </span>
        </div>
      </div>

      <div
        className="beneficiary-gantt__scroll"
        role="region"
        aria-label="Timeline chart — scroll horizontally to see the full axis and labels"
        tabIndex={0}
      >
        <div
          className={
            "beneficiary-gantt__body" + (hasPortalPhotoColumn ? " beneficiary-gantt__body--with-portal-photos" : "")
          }
        >
          <div className="beneficiary-gantt__labels">
            <div className="beneficiary-gantt__corner" aria-hidden />
            {rows.map((r) => (
              <div key={r.id} className="beneficiary-gantt__label">
                {r.title}
              </div>
            ))}
          </div>

          <div className="beneficiary-gantt__tracks">
            <div className="beneficiary-gantt__axis">
              {ticks.map((d) => (
                <span
                  key={d}
                  className="beneficiary-gantt__tick"
                  style={{ left: `${axisMax > 0 ? (d / axisMax) * 100 : 0}%` }}
                >
                  {d}
                </span>
              ))}
            </div>

            {rows.map((r) => (
              <div key={r.id} className="beneficiary-gantt__track">
                <div
                  className={
                    "beneficiary-gantt__bar" + (r.actual != null ? " beneficiary-gantt__bar--has-actual" : "")
                  }
                  style={{ left: `${r.leftPct}%`, width: `${r.widthPct}%` }}
                  aria-hidden
                />
                <span
                  className="beneficiary-gantt__caption"
                  style={{ left: `calc(${r.leftPct}% + ${r.widthPct}% + 0.35rem)` }}
                >
                  <span className="beneficiary-gantt__cap-approx">{formatApproxDays(r.approxDays)}</span>
                  <span className="beneficiary-gantt__cap-sep" aria-hidden>
                    {" · "}
                  </span>
                  <span className="beneficiary-gantt__cap-actual">
                    Actual {r.actual == null ? "—" : `${r.actual} d`}
                  </span>
                </span>
              </div>
            ))}
          </div>

          {hasPortalPhotoColumn ? (
            <div className="beneficiary-gantt__portal-photos" aria-label="Subcontractor portal photos by phase">
              <div className="beneficiary-gantt__corner beneficiary-gantt__portal-photos__corner" aria-hidden />
              {rows.map((r) => (
                <div key={r.id} className="beneficiary-gantt__photo-cell">
                  <GanttPortalThumbStrip bundle={phasePortalPhotos?.[r.id]} />
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}

export function BeneficiaryDedicatedTimeline({ app }: { app: Application }) {
  const { constructionTimelinePhases } = useAppState();
  const sorted = useMemo(() => sortConstructionPhases(constructionTimelinePhases), [constructionTimelinePhases]);
  const [phasePortalPhotos, setPhasePortalPhotos] = useState<Partial<Record<string, PhasePortalPhotoBundle>>>({});

  useEffect(() => {
    let cancelled = false;
    void fetchBeneficiaryTimelineProgress(app.id)
      .then((rows) => {
        if (cancelled) return;
        const m: Partial<Record<string, PhasePortalPhotoBundle>> = {};
        for (const r of rows) {
          if (r.photo_data || r.photo_data_2 || r.photo_data_3) {
            m[r.timeline_phase_id] = {
              photo1: r.photo_data,
              photo2: r.photo_data_2,
              photo3: r.photo_data_3,
            };
          }
        }
        setPhasePortalPhotos(m);
      })
      .catch(() => {
        if (!cancelled) setPhasePortalPhotos({});
      });
    return () => {
      cancelled = true;
    };
  }, [app.id]);

  return (
    <ConstructionGanttView
      phases={sorted}
      actualDays={app.constructionTimelineActualDays ?? {}}
      phasePortalPhotos={phasePortalPhotos}
    />
  );
}
